/**
 * Theme application + persistence. The header cycle button calls into this
 * module; the editor host subscribes to `currentTheme` so it can call
 * `scene.applyTheme()` on every flip (CSS retints panels by itself, but the
 * Pixi canvas needs an explicit redraw — same pattern as the Phase 2
 * duplicate-render fix).
 */
import { get, writable, type Readable } from 'svelte/store'
import * as settingsStore from '$lib/storage/settings'
import type { Theme } from '$lib/types'
import { THEMES, nextTheme } from './themes'

const _currentTheme = writable<Theme>('dark')

/** Read-only handle for components that want to react to theme changes. */
export const currentTheme: Readable<Theme> = { subscribe: _currentTheme.subscribe }

function setBodyClass(theme: Theme): void {
  if (typeof document === 'undefined') return
  for (const t of THEMES) {
    document.body.classList.remove(t.cls)
  }
  document.body.classList.add(`theme-${theme}`)
}

/**
 * Apply a theme synchronously. Sets the body class and the in-memory store;
 * does NOT persist. Used at app boot to install the saved theme without a
 * round-trip back through Dexie.
 */
export function applyThemeSync(theme: Theme): void {
  setBodyClass(theme)
  _currentTheme.set(theme)
}

/**
 * Apply a theme and persist to settings. Call this from the header cycle
 * button or the (future) settings dropdown. CSS retints automatically; any
 * subscriber to `currentTheme` (e.g. the editor host) is responsible for
 * propagating the change to non-CSS surfaces.
 */
export async function applyTheme(theme: Theme): Promise<void> {
  setBodyClass(theme)
  _currentTheme.set(theme)
  await settingsStore.set({ theme })
}

/** Advance to the next theme in the canonical cycle order, persist, return it. */
export async function cycleTheme(): Promise<Theme> {
  const next = nextTheme(get(_currentTheme))
  await applyTheme(next)
  return next
}
